import { Injectable } from "@angular/core";
import { AuthService } from "@auth0/auth0-angular";
import { BehaviorSubject, Observable, combineLatest, of, throwError } from "rxjs";
import {
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  tap,
} from "rxjs/operators";
import { filter } from "rxjs/operators";

import { SSOService } from "./sso.service";
import { SharedService } from "../../shared/shared.service";
import { VirtualCheckupHandoffService } from "../../virtual-checkup-handoff/virtual-checkup-handoff.service";

/**
 * Authentication Types
 */
export enum AuthType {
  NONE = "none",
  AUTH0 = "auth0",
  SSO = "sso",
  /** Opaque server minted token for mobile virtual checkup (not Auth0). */
  VIRTUAL_CHECKUP_HANDOFF = "virtual_checkup_handoff",
}

/**
 * Unified Authentication State
 */
export interface UnifiedAuthState {
  isAuthenticated: boolean;
  isProcessing?: boolean; // Add this
  authType: AuthType;
  user?: any;
  email?: string;
}

/**
 * Integrated Auth Service
 * Provides unified authentication state management for both Auth0 and SSO
 * Determines which authentication method is active and provides consistent interface
 */
@Injectable({
  providedIn: "root",
})
export class IntegratedAuthService {
  private authStateSubject = new BehaviorSubject<UnifiedAuthState>({
    isAuthenticated: false,
    authType: AuthType.NONE,
    isProcessing: false,
  });

  public authState$ = this.authStateSubject.asObservable();

  // Unified authentication status
  public readonly isAuthenticated$ = combineLatest([
    this.authState$,
    this.ssoService.ssoState$,
    this.auth0.isLoading$,
    this.handoffService.isActive$,
  ]).pipe(
    filter(
      ([_authState, ssoState, auth0Loading, handoffActive]) =>
        handoffActive || (!ssoState.isLoading && !auth0Loading)
    ),
    switchMap(([authState, ssoState, , handoffActive]) => {
      if (handoffActive) {
        return of(true);
      }
      if (authState.isAuthenticated) {
        return of(authState.isAuthenticated);
      }
      if (authState.authType === AuthType.SSO || ssoState.isAuthenticated) {
        // SSO - immediate response
        return of(ssoState.isAuthenticated);
      }

      return this.auth0.isAuthenticated$;
    }),
    distinctUntilChanged(),
    shareReplay(1)
  );

  // Current authentication type
  public authType$ = this.authState$.pipe(map((state) => state.authType));

  // User information (from either Auth0 or SSO)
  public user$ = this.authState$.pipe(map((state) => state.user));

  // User email (from either Auth0 or SSO)
  public email$ = this.authState$.pipe(map((state) => state.email));

  constructor(
    private auth0: AuthService,
    private ssoService: SSOService,
    private sharedService: SharedService,
    private handoffService: VirtualCheckupHandoffService
  ) {
    this.restoreHandoffAuthIfNeeded();
    this.initializeAuthState();
  }

  private restoreHandoffAuthIfNeeded(): void {
    if (!this.handoffService.isActive()) {
      return;
    }
    const email = this.handoffService.getEmail();
    if (email) {
      this.applyVirtualCheckupHandoffSession(email);
    }
  }

  /**
   * Called after mobile landing page validates the handoff token (sessionStorage is set).
   */
  applyVirtualCheckupHandoffSession(email: string): void {
    this.authStateSubject.next({
      isAuthenticated: true,
      authType: AuthType.VIRTUAL_CHECKUP_HANDOFF,
      user: { email },
      email,
    });
  }

  /** After successful scan submit, revoke handoff client-side (token already consumed server-side). */
  endVirtualCheckupHandoffSession(): void {
    this.handoffService.clear();
    if (this.getAuthType() === AuthType.VIRTUAL_CHECKUP_HANDOFF) {
      this.authStateSubject.next(this.createUnauthenticatedState());
    }
  }

  /**
   * Initialize combined authentication state monitoring
   */
  private initializeAuthState(): void {
    // Monitor both Auth0 and SSO authentication states
    combineLatest([
      this.auth0.isAuthenticated$,
      this.auth0.user$,
      this.ssoService.ssoState$,
    ])
      .pipe(
        filter(() => !this.handoffService.isActive()),
        filter(([auth0Authenticated, auth0User, ssoState]) => {
          // Don't emit anything during callback processing
          const isAuth0Callback = this.isAuth0CallbackInProgress();
          if (
            !ssoState.isAuthenticated &&
            !auth0Authenticated &&
            isAuth0Callback
          ) {
            return false; // Don't emit - auth0 callback is in progress
          }
          return true;
        }),
        map(([auth0Authenticated, auth0User, ssoState]) => {
          if (ssoState.isAuthenticated) {
            return this.createSSOAuthState();
          } else if (auth0Authenticated && auth0User) {
            return this.createAuth0AuthState(auth0User);
          } else {
            return this.createUnauthenticatedState();
          }
        }),
        distinctUntilChanged((prev, curr) => {
          return (
            prev.isAuthenticated === curr.isAuthenticated &&
            prev.authType === curr.authType &&
            prev.isProcessing === curr.isProcessing
          );
        }),
        tap((authState) => {
          this.authStateSubject.next(authState);
        })
      )
      .subscribe();
  }

  /**
   * Create authentication state for SSO
   */
  private createSSOAuthState(): UnifiedAuthState {
    const tokens = this.ssoService.getStoredTokens();
    let userInfo = null;
    let email = null;

    if (tokens && tokens.id_token) {
      try {
        const base64Url = tokens.id_token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        );
        userInfo = JSON.parse(jsonPayload);
        email = userInfo.email;
      } catch (error) {
        console.error("Failed to parse SSO token:", error);
      }
    }

    return {
      isAuthenticated: true,
      authType: AuthType.SSO,
      user: userInfo,
      email: email,
    };
  }

  /**
   * Create authentication state for Auth0
   */
  private createAuth0AuthState(auth0User: any): UnifiedAuthState {
    return {
      isAuthenticated: true,
      authType: AuthType.AUTH0,
      user: auth0User,
      email: auth0User.email,
    };
  }

  /**
   * Create unauthenticated state
   */
  private createUnauthenticatedState(): UnifiedAuthState {
    return {
      isAuthenticated: false,
      authType: AuthType.NONE,
    };
  }

  /**
   * Get current authentication type
   */
  public getAuthType(): AuthType {
    return this.authStateSubject.value.authType;
  }

  /**
   * Access token for explicit Bearer headers (e.g. virtual checkup handoff mint).
   * SSO sessions store Auth0 tokens outside the Auth0 SDK cache.
   */
  public getApiAccessToken$(): Observable<string> {
    const ssoTokens = this.ssoService.getStoredTokens();
    if (ssoTokens?.access_token) {
      return of(ssoTokens.access_token);
    }

    if (this.getAuthType() === AuthType.VIRTUAL_CHECKUP_HANDOFF) {
      return throwError(
        () => new Error("Handoff session cannot mint another handoff")
      );
    }

    return this.auth0.getAccessTokenSilently();
  }

  /**
   * Unified logout functionality
   * Clears both Auth0 and SSO authentication states
   */
  public logout(options?: any): void {
    const currentAuthType = this.getAuthType();

    this.handoffService.clear();

    // Clear SSO state
    this.ssoService.logout();

    // Clear portal information
    this.sharedService.localStoragePortal = null;

    if (currentAuthType === AuthType.VIRTUAL_CHECKUP_HANDOFF) {
      window.location.href = options?.returnTo || window.location.origin;
      return;
    }

    // Handle Auth0 logout if applicable
    if (currentAuthType === AuthType.AUTH0) {
      this.auth0.logout({
        returnTo: options?.returnTo || window.location.origin,
      });
    } else {
      // For SSO logout, redirect to home page
      window.location.href = options?.returnTo || window.location.origin;
    }
  }

  /**
   * Login with redirect (Auth0)
   * For SSO, users come through the /sso route
   */
  public loginWithRedirect(options?: any): void {
    this.auth0.loginWithRedirect({
      screen_hint: "signup",
      ...options,
    });
  }

  /**
   * Helper method to check if Auth0 callback is in progress
   */
  private isAuth0CallbackInProgress(): boolean {
    const url = new URL(window.location.href);
    return (
      url.searchParams.has("code") &&
      url.searchParams.has("state") &&
      !url.pathname.includes("/sso")
    );
  }
}
